import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Code2, Layers, Wrench, ArrowLeft } from "lucide-react";
import { getWhatsAppChatUrl } from "../../config/public-site";
import EcommerceHeader, { EcommerceFooter, WhatsAppFab } from "../../components/ecommerce/EcommerceHeader";
import { EC } from "../../utils/const/ecommerce-colors-const";
import profilePhoto from "../../assets/profile_picture.png";

const DEVELOPER = {
    fullName: "Jose Natalio Tapia Marca",
    companyEmail: "tapia.jose.natalio@gmail.com",
    phone: "+59176697125",
    email: "tapia.jose.natalio@gmail.com",
    addressLine1: "ZONA PLAN 3000 BARRIO CRISTAL CALLE SN",
    cityRegion: "Santa Cruz - Bolivia",
    zipCode: "0000",
};

export default function ProfilePage() {
    const history = useHistory();
    const { t } = useTranslation();
    const [photoSrc, setPhotoSrc] = useState(profilePhoto);
    const whatsappUrl = getWhatsAppChatUrl();
    const currentYear = new Date().getFullYear();

    const services = [
        { icon: Code2, textKey: "dev_profile_service_1" },
        { icon: Layers, textKey: "dev_profile_service_2" },
        { icon: Wrench, textKey: "dev_profile_service_3" },
    ];

    const handleNavClick = () => {
        history.push("/landing");
    };

    return (
        <div className={EC.page.root}>
            <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
                <div className={`absolute -top-40 -right-32 h-96 w-96 rounded-full blur-3xl ${EC.page.blobPrimary}`} />
                <div className={`absolute top-1/3 -left-24 h-80 w-80 rounded-full blur-3xl ${EC.page.blobPrice}`} />
            </div>

            <EcommerceHeader onNavClick={handleNavClick} activeSection={null} />

            <main className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24">
                <div className="max-w-4xl mx-auto">
                    <button
                        type="button"
                        onClick={() => history.push("/landing")}
                        className={`inline-flex items-center gap-2 mb-6 ${EC.form.link}`}
                    >
                        <ArrowLeft className="w-4 h-4" aria-hidden />
                        {t("dev_profile_back")}
                    </button>

                    <span className={`inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full text-sm font-semibold ${EC.hero.badge}`}>
                        <span aria-hidden>💻</span>
                        {t("dev_profile_badge")}
                    </span>

                    <div className={EC.form.card}>
                        <div className={EC.form.cardGlow} />

                        <div className="relative">
                            <div className="flex flex-col md:flex-row gap-8 md:gap-10 items-center md:items-start">
                                <div className="shrink-0">
                                    <div className={`w-36 h-36 sm:w-44 sm:h-44 rounded-3xl overflow-hidden border-4 border-white shadow-lg bg-ct-primary-50 ${EC.hero.imageRing}`}>
                                        <img
                                            src={photoSrc}
                                            alt={DEVELOPER.fullName}
                                            className="w-full h-full object-cover"
                                            onError={() => setPhotoSrc(profilePhoto)}
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 text-center md:text-left min-w-0">
                                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                                        {DEVELOPER.fullName}
                                    </h1>
                                    <p className={`mt-2 text-lg sm:text-xl font-semibold ${EC.brand.text}`}>
                                        {t("dev_profile_role")}
                                    </p>
                                    <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl">
                                        {t("dev_profile_subtitle")}
                                    </p>
                                    <p className="mt-4 text-sm sm:text-base text-slate-500 leading-relaxed max-w-2xl">
                                        {t("dev_profile_about")}
                                    </p>

                                    <div className="mt-6 flex flex-col sm:flex-row flex-wrap justify-center md:justify-start gap-3">
                                        <a
                                            href={`mailto:${DEVELOPER.email}`}
                                            className={`inline-flex justify-center items-center gap-2 px-6 py-3 rounded-xl text-sm ${EC.btn.primaryLg}`}
                                        >
                                            <Mail className="w-4 h-4" aria-hidden />
                                            {t("dev_profile_cta_email")}
                                        </a>
                                        <a
                                            href={`tel:${DEVELOPER.phone.replace(/\s/g, "")}`}
                                            className={`inline-flex justify-center items-center gap-2 px-6 py-3 rounded-xl text-sm ${EC.btn.secondary}`}
                                        >
                                            <Phone className={`w-4 h-4 ${EC.icon.primary}`} aria-hidden />
                                            {t("dev_profile_cta_phone")}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <section className="mt-10 pt-8 border-t border-slate-100">
                                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-4">
                                    {t("dev_profile_services_title")}
                                </h2>
                                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {services.map(({ icon: Icon, textKey }) => (
                                        <li
                                            key={textKey}
                                            className={`flex items-start gap-3 p-4 ${EC.card.base}`}
                                        >
                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-ct-primary-100 ${EC.icon.primary}`}>
                                                <Icon className="w-5 h-5" aria-hidden />
                                            </span>
                                            <span className="text-sm font-medium text-slate-700 pt-1.5">
                                                {t(textKey)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            <section className="mt-10 pt-8 border-t border-slate-100">
                                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-5">
                                    {t("dev_profile_contact_title")}
                                </h2>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <ContactCard
                                        icon={Mail}
                                        label={t("dev_profile_company_email")}
                                        value={DEVELOPER.companyEmail}
                                        href={`mailto:${DEVELOPER.companyEmail}`}
                                    />
                                    <ContactCard
                                        icon={Mail}
                                        label={t("dev_profile_email")}
                                        value={DEVELOPER.email}
                                        href={`mailto:${DEVELOPER.email}`}
                                    />
                                    <ContactCard
                                        icon={Phone}
                                        label={t("dev_profile_phone")}
                                        value={DEVELOPER.phone}
                                        href={`tel:${DEVELOPER.phone.replace(/\s/g, "")}`}
                                    />
                                    <ContactCard
                                        icon={MapPin}
                                        label={t("dev_profile_address")}
                                        value={
                                            <>
                                                {DEVELOPER.addressLine1}
                                                <br />
                                                {DEVELOPER.cityRegion}
                                                <br />
                                                <span className="text-slate-500">
                                                    {t("dev_profile_zip")}: {DEVELOPER.zipCode}
                                                </span>
                                            </>
                                        }
                                    />
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            <EcommerceFooter t={t} whatsappUrl={whatsappUrl} currentYear={currentYear} />
            <WhatsAppFab whatsappUrl={whatsappUrl} ariaLabel={t("te_whatsapp_fab_aria")} />
        </div>
    );
}

function ContactCard({ icon: Icon, label, value, href }) {
    const content = (
        <div className={`flex gap-3 p-4 sm:p-5 h-full ${EC.card.base} ${EC.card.hover}`}>
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ct-primary-50 ${EC.icon.primary}`}>
                <Icon className="w-5 h-5" aria-hidden />
            </span>
            <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-sm font-medium text-slate-800 break-words">{value}</p>
            </div>
        </div>
    );

    if (href) {
        return (
            <a
                href={href}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ct-primary-500 rounded-2xl"
            >
                {content}
            </a>
        );
    }

    return content;
}
